import React from "react";
import { Button, Grid, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import authService from "../services/auth-service";

const logout = () => {
	authService.logout();
	window.location.assign("/login");
};

const getGraphQLErrorCode = (error) => {
	const graphQLError = error?.graphQLErrors?.[0];
	return graphQLError?.extensions?.code || graphQLError?.extensions?.exception?.code;
};

const getNetworkStatusCode = (error) => {
	const networkError = error?.networkError;
	return (
		networkError?.statusCode ||
		networkError?.status ||
		networkError?.response?.status ||
		networkError?.result?.statusCode
	);
};

const getUserFacingError = (error, t) => {
	const graphQLErrorCode = getGraphQLErrorCode(error);
	const networkStatusCode = getNetworkStatusCode(error);
	const rawMessage =
		error?.graphQLErrors?.[0]?.message ||
		error?.networkError?.result?.errors?.[0]?.message ||
		error?.networkError?.result?.message ||
		error?.networkError?.message ||
		error?.message ||
		t("ERROR.UNKNOWN");

	if (graphQLErrorCode === "UNAUTHENTICATED" || networkStatusCode === 401) {
		return {
			title: t("ERROR.INVALID_SESSION_TITLE"),
			description: t("ERROR.INVALID_SESSION_DESCRIPTION"),
			showLogout: true,
		};
	}

	if ([502, 503, 504].includes(networkStatusCode)) {
		return {
			title: t("ERROR.SERVER_UNAVAILABLE_TITLE"),
			description: t("ERROR.SERVER_UNAVAILABLE_DESCRIPTION"),
			details: `HTTP ${networkStatusCode}`,
			showRetry: true,
		};
	}

	if (networkStatusCode === 429 || rawMessage.toLowerCase().includes("too many")) {
		return {
			title: t("ERROR.TOO_MANY_REQUESTS_TITLE"),
			description: t("ERROR.TOO_MANY_REQUESTS_DESCRIPTION"),
			details: networkStatusCode ? `HTTP ${networkStatusCode}` : rawMessage,
			showRetry: true,
		};
	}

	if (
		rawMessage.includes("Failed to fetch") ||
		rawMessage.includes("NetworkError") ||
		rawMessage.includes("Network Error") ||
		rawMessage.includes("ECONNREFUSED")
	) {
		return {
			title: t("ERROR.CONNECTION_FAILED_TITLE"),
			description: t("ERROR.CONNECTION_FAILED_DESCRIPTION"),
			details: rawMessage,
			showRetry: true,
		};
	}

	return {
		title: t("ERROR.LOAD_DATA_TITLE"),
		description: t("ERROR.LOAD_DATA_DESCRIPTION"),
		details: rawMessage,
		showRetry: true,
	};
};

function ErrorScreen({ error, onRetry }) {
	const { t } = useTranslation();
	const { title, description, details, showLogout, showRetry } =
		getUserFacingError(error, t);

	return (
		<Grid
			container
			spacing={2}
			alignItems="center"
			justifyContent={"center"}
			direction="column"
			height="80vh"
			sx={{
				bgcolor: "#0c0c0e",
				m: 0,

				width: "100vw",
			}}>
			<Grid item>
				<Typography variant="h4" align="center">
					{title}
				</Typography>
			</Grid>
			<Grid item>
				<Typography variant="body1" align="center" sx={{ maxWidth: 640 }}>
					{description}
				</Typography>
			</Grid>
			{details ? (
				<Grid item>
					<Typography
						variant="body2"
						align="center"
						sx={{ maxWidth: 760, opacity: 0.75 }}
					>
						{details}
					</Typography>
				</Grid>
			) : null}
			<Grid item>
				<Stack direction="row" spacing={2}>
					{showRetry && onRetry ? (
						<Button variant="contained" onClick={onRetry}>
							{t("ACTIONS.RETRY")}
						</Button>
					) : null}
					{showLogout ? (
						<Button variant="outlined" onClick={logout}>
							{t("ACTIONS.BACK_TO_LOGIN")}
						</Button>
					) : null}
				</Stack>
			</Grid>
		</Grid>
	);
}
export default ErrorScreen;
